// src/pages/HomePage.js — v3 compendium
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ONI_FORMS, PERKS_LIST, ONI_IMG_VERSION} from "../data/demons";
import { useAuth } from "../hooks/useAuth";
import "./HomePage.css";

export default function HomePage() {
  const revealRefs = useRef([]);
  const addR = el => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };
  const { profile } = useAuth();
  const hasDemon = !!(profile?.demon);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.08, rootMargin:"0px 0px -30px 0px" }
    );
    revealRefs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="homepage">

      {/* HERO */}
      <section className="hero">
        <div className="hero-bg-glow"/>
        <div className="hero-kanji-bg" aria-hidden><span>鬼</span></div>
        <div className="hero-pillars" aria-hidden>
          <span className="pillar">卓球鬼道場激怒破壊魂</span>
          <span className="pillar">悪魔的技術混沌の力血</span>
          <span className="pillar">鬼の覚醒燃える魂戦士</span>
          <span className="pillar">怒り破壊卓球鬼道地獄</span>
        </div>
        <div className="hero-content">
          <p className="hero-eyebrow">卓球鬼 · LIFE WEAR · DEPUIS L'ENFER DU PING</p>
          <div className="hero-logo-wrap">
            <img src="/logo-takkyu.png" alt="Takkyu Oni" className="hero-logo-img"/>
          </div>
          <p className="hero-sub">AWAKEN YOUR DEMON</p>
          <p className="hero-tagline">
            Quelque part entre le filet qui te vole le point et le mec qui fait le smasher
            à l'entraînement — un démon dort en toi. Il est temps de le nommer.
          </p>
          <div className="hero-cta">
            {hasDemon ? (
              <>
                <Link to="/profile" className="btn btn-primary btn-lg">
                  {profile.demon.oni?.emoji} MON DÉMON →
                </Link>
                <Link to="/journal" className="btn btn-ghost">ONITALK 💬</Link>
              </>
            ) : (
              <>
                <Link to="/awaken" className="btn btn-primary btn-lg">RÉVEILLER MON DÉMON →</Link>
                <Link to="/lore" className="btn btn-ghost">DÉCOUVRIR LA LÉGENDE</Link>
              </>
            )}
          </div>
        </div>
        <div className="hero-scroll" aria-hidden>
          <span>SCROLL</span>
          <div className="scroll-line"/>
        </div>
      </section>

      {/* LORE TEASER */}
      <section className="section">
        <div ref={addR} className="reveal lore-teaser">
          <div className="lore-teaser-text">
            <span className="section-eyebrow">伝説 · La Légende</span>
            <h2 className="section-title">L'Origine<br/>des Oni</h2>
            <div className="divider"/>
            <p>Il est dit que le premier coup de raquette frappé dans la <em>rage pure</em> ouvrit une porte. Pas une porte vers la victoire — une porte vers l'autre côté.</p>
            <p style={{marginTop:"1rem"}}>De l'autre côté vivent les <em>Takkyu Oni</em> : démons nés de chaque mauvaise excuse, de chaque service illégal assumé, de chaque rouleau sur le filet en finale. Ils sont toi.</p>
            <Link to="/lore" className="btn btn-ghost btn-sm" style={{marginTop:"2rem"}}>LIRE LA LÉGENDE COMPLÈTE →</Link>
          </div>
          <div className="lore-scroll-card">
            <span className="scroll-mark">鬼</span>
            <p className="scroll-jp">「卓球の鬼は怒りから生まれる。<br/>しかし、その怒りこそが力だ。<br/>自分の鬼を知る者だけが、<br/>真の卓球師になれる。」</p>
            <p className="scroll-fr">"Le démon du ping naît de la colère.<br/>Mais cette colère est sa force.<br/>Seul celui qui connaît son démon<br/>peut devenir un vrai maître de la balle."</p>
            <cite>— AINO, Démon en Chef · 悪魔長</cite>
          </div>
        </div>
      </section>

      {/* ONI FORMS */}
      <section className="oni-preview-section">
        <div className="section">
          <div ref={addR} className="reveal" style={{textAlign:"center", marginBottom:"3rem"}}>
            <span className="section-eyebrow">原型 · Les Formes</span>
            <h2 className="section-title">Les 12 Oni</h2>
            <div className="divider divider-center"/>
          </div>
          <div className="oni-grid">
            {ONI_FORMS.map((oni, i) => (
              <div key={oni.id} ref={addR} className="reveal oni-card" style={{transitionDelay:`${i*0.04}s`}}>
                {oni.image ? (
                  <div className="oni-card-img-wrap">
                    <img src={`${oni.image}?v=${ONI_IMG_VERSION}`} alt={oni.name} className="oni-card-img"/>
                    <div className="oni-card-img-overlay"/>
                  </div>
                ) : (
                  <span className="oni-emoji">{oni.emoji}</span>
                )}
                <span className="oni-jp">{oni.jp}</span>
                <span className="oni-name">{oni.name}</span>
                <span className="oni-sign">{oni.sign}</span>
                <span className="oni-tagline">{oni.tagline}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERKS TICKER */}
      <div className="perks-ticker" aria-hidden>
        <div className="ticker-track">
          {[...PERKS_LIST,...PERKS_LIST].map((p,i) => (
            <span key={i} className="ticker-item">⚡ {p.label}</span>
          ))}
        </div>
      </div>

      {/* LE PARCOURS */}
      <section className="journey-section">
        <div className="section">
          <div ref={addR} className="reveal" style={{textAlign:"center", marginBottom:"3.5rem"}}>
            <span className="section-eyebrow">道 · Le Parcours</span>
            <h2 className="section-title">De toi<br/>à ton démon</h2>
            <div className="divider divider-center"/>
          </div>

          <div className="journey-grid">
            {/* Étape 1 — Quiz */}
            <div ref={addR} className="reveal journey-step">
              <div className="journey-step-num">01</div>
              <div className="journey-img-wrap">
                <img src="/oni/shark.jpg?v=3" alt="Ton Oni apparaît" className="journey-img"/>
                <div className="journey-img-overlay"/>
              </div>
              <h3 className="journey-step-title">Réponds. Découvre.</h3>
              <p className="journey-step-text">
                5 questions sur ton style, ton mental, tes excuses préférées.
                Ton démon n'attendait que ça pour se révéler.
              </p>
            </div>

            {/* Étape 2 — OniTalk fait évoluer le démon */}
            <div ref={addR} className="reveal journey-step" style={{transitionDelay:"0.1s"}}>
              <div className="journey-step-num">02</div>
              <div className="journey-img-wrap">
                <img src="/oni/owl.jpg?v=3" alt="Ton démon évolue" className="journey-img"/>
                <div className="journey-img-overlay"/>
              </div>
              <h3 className="journey-step-title">Raconte. Évolue.</h3>
              <p className="journey-step-text">
                Chaque session que tu confies à OniTalk transforme ton démon —
                ses stats bougent, sa chronique s'écrit, son histoire devient la tienne.
              </p>
            </div>

            {/* Étape 3 — Merch */}
            <div ref={addR} className="reveal journey-step" style={{transitionDelay:"0.2s"}}>
              <div className="journey-step-num">03</div>
              <div className="journey-img-wrap">
                <img src="/images/merch-tshirt-preview.jpg" alt="Ton démon sur du merch" className="journey-img"/>
                <div className="journey-img-overlay"/>
              </div>
              <h3 className="journey-step-title">Porte-le. Affiche-le.</h3>
              <p className="journey-step-text">
                Ton démon, ton nom, ton t-shirt. Imprimé à la demande,
                livré chez toi — la preuve qu'il existe vraiment.
              </p>
            </div>
          </div>

          <div ref={addR} className="reveal" style={{textAlign:"center", marginTop:"3rem"}}>
            <Link to="/merch" className="btn btn-ghost">VOIR LA BOUTIQUE →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="section" style={{textAlign:"center"}}>
          <div ref={addR} className="reveal">
            <span className="section-eyebrow">覚醒 · Le Réveil</span>
            <h2 className="section-title">Prêt à affronter<br/>ton démon ?</h2>
            <div className="divider divider-center"/>
            <p style={{color:"var(--ash)", maxWidth:"480px", margin:"0 auto 2.5rem", lineHeight:2.1, fontSize:"0.83rem"}}>
              5 questions. Tes perks. Ton archétype Oni.<br/>
              Un profil démoniaque qui évolue avec toi.
            </p>
            <Link to="/awaken" className="btn btn-primary btn-lg">COMMENCER LE QUIZ →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
